import { MENU_ITEM } from "../types";

export interface MenuSection {
  title: string;
  items: MENU_ITEM[];
}

/**
 * Groups a flat menu item list into sections, preserving array order.
 *
 * A submenu is either fully sectioned or flat: if any item lacks a `section`,
 * this returns no sections and the caller falls back to a flat render. That
 * keeps untagged submenus (Deliveries/Office/Team) rendering exactly as before,
 * and makes a half-tagged Configuration list fail loudly in tests rather than
 * render a half-grouped menu.
 */
export function groupItemsIntoSections(items: MENU_ITEM[]): MenuSection[] {
  const sections: MenuSection[] = [];

  for (const item of items) {
    const title = item.section;
    if (!title) return [];

    const existingSection = sections.find(
      (menuSection) => menuSection.title === title
    );
    if (existingSection) {
      existingSection.items.push(item);
      continue;
    }
    sections.push({ title, items: [item] });
  }

  return sections;
}

function sectionRowCount(section: MenuSection): number {
  return 1 + section.items.length; // one header row, plus one row per item
}

/**
 * Splits sections across two columns by choosing the split index that minimizes
 * the taller column, while preserving array order. A section is never split
 * across columns.
 *
 * Order preservation is the requirement, so a greedy shortest-column fill is
 * deliberately not used: it balances marginally better but scrambles reading
 * order (it would place Automation and Operations left, Billing and Data right).
 */
export function splitSectionsIntoColumns(
  sections: MenuSection[]
): [MenuSection[], MenuSection[]] {
  if (sections.length < 2) return [sections, []];

  const rowCounts = sections.map(sectionRowCount);
  const totalRows = rowCounts.reduce((sum, count) => sum + count, 0);

  let bestSplitIndex = 1;
  let bestTallerColumn = Number.POSITIVE_INFINITY;
  let leftRows = 0;

  for (let splitIndex = 1; splitIndex < sections.length; splitIndex++) {
    leftRows += rowCounts[splitIndex - 1];
    const tallerColumn = Math.max(leftRows, totalRows - leftRows);
    if (tallerColumn < bestTallerColumn) {
      bestTallerColumn = tallerColumn;
      bestSplitIndex = splitIndex;
    }
  }

  return [sections.slice(0, bestSplitIndex), sections.slice(bestSplitIndex)];
}
